/**
 * NotificationUI Component
 *
 * Global notification display component that reads from the notification atom
 * and displays toast-style notifications with auto-hide functionality.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  useNotification,
  useNotificationValue,
} from '@gaddario98/react-core/notifications'
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react'
import { cn, useTranslation } from '@/core'

export interface NotificationUIProps {
  className?: string
  defaultAutoHideDuration?: number
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

const positionClasses = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
}

const typeStyles = {
  success: {
    container: 'bg-green-50 border-green-200 text-green-800',
    icon: 'text-green-600',
  },
  error: {
    container: 'bg-red-50 border-red-200 text-red-800',
    icon: 'text-red-600',
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    icon: 'text-yellow-600',
  },
  info: {
    container: 'bg-blue-50 border-blue-200 text-blue-800',
    icon: 'text-blue-600',
  },
}

const typeIcons = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
}

export function NotificationUI({
  className,
  defaultAutoHideDuration = 5000,
  position = 'top-right',
}: NotificationUIProps) {
  const notification = useNotificationValue()
  const { clearNotification } = useNotification('')
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)
  const handleClose = useCallback(() => {
    setIsLeaving(true)
    setTimeout(() => {
      setVisible(false)
      setIsLeaving(false)
      clearNotification()
    }, 200) // Animation duration
  }, [clearNotification])

  useEffect(() => {
    if (notification) {
      setVisible(true)
      setIsLeaving(false)
      const duration = notification.autoHideDuration ?? defaultAutoHideDuration
      if (duration > 0) {
        const timer = setTimeout(handleClose, duration)
        return () => clearTimeout(timer)
      }
    }
  }, [notification, defaultAutoHideDuration, handleClose])

  if (!notification || !visible) return null

  const styles = typeStyles[notification.type]
  const Icon = typeIcons[notification.type]

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'fixed z-80 max-w-md p-4 rounded-lg border shadow-lg',
        'transition-all duration-200 ease-out',
        isLeaving
          ? 'opacity-0 translate-y-2'
          : 'opacity-100 translate-y-0',
        positionClasses[position],
        styles.container,
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className={cn('size-5 flex-shrink-0 mt-0.5', styles.icon)} />
        <p className="text-sm font-medium flex-1">
          {t(notification.message)}
        </p>
        <button
          onClick={handleClose}
          className={cn(
            'flex-shrink-0 p-1 rounded-md',
            'hover:bg-black/5 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            notification.type === 'success' && 'focus:ring-green-500',
            notification.type === 'error' && 'focus:ring-red-500',
            notification.type === 'warning' && 'focus:ring-yellow-500',
            notification.type === 'info' && 'focus:ring-blue-500',
          )}
          aria-label="Chiudi notifica"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  )
}

export default NotificationUI
